/**
 * Video Loop Player
 * 
 * A loop player for hosted video files (MP4, WebM, etc.).
 * Uses native HTML5 <video> element with the same UI/UX as youtube-loop-player.
 * 
 * @element video-loop-player
 * 
 * @prop {string} src - URL to the video file (required)
 * @prop {string} poster - Poster image URL (optional)
 * @prop {string} preload - Preload behavior: 'none', 'metadata', 'auto' (default: 'metadata')
 * @prop {boolean} showShortcuts - Show keyboard shortcuts help (default: true)
 * @prop {boolean} autoRestore - Restore saved state from localStorage (default: true)
 * @prop {string} storagePrefix - Prefix for storage keys (default: 'videoloop')
 * 
 * @fires loop-created - When a new loop is created
 * @fires loop-deleted - When a loop is deleted
 * @fires playback-state-changed - When play/pause state changes
 */

import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { LoopPlayerMixin, loopPlayerStyles } from '../loop-player-base/index.js';

@customElement('video-loop-player')
export class VideoLoopPlayer extends LoopPlayerMixin(LitElement) {
    static styles = [
        ...loopPlayerStyles,
        css`
      /* Video-specific styles */
      .lp-media-wrapper video {
        object-fit: contain;
      }
    `
    ];

    // ─── Public Properties ───────────────────────────────────────────────────

    @property({ type: String })
    src = '';

    @property({ type: String })
    poster = '';

    @property({ type: String })
    preload: 'none' | 'metadata' | 'auto' = 'metadata';

    @property({ type: Boolean })
    showShortcuts = true;

    @property({ type: Boolean })
    autoRestore = true;

    @property({ type: String })
    storagePrefix = 'videoloop';

    /** Hide padding and use minimal styling (for embedded contexts) */
    @property({ type: Boolean })
    compact = false;

    // ─── Private State ───────────────────────────────────────────────────────

    @state() private _mediaReady = false;

    @query('video') private _video!: HTMLVideoElement;

    // ─── Abstract Implementation: Compact View ───────────────────────────────

    readonly mediaIcon = '🎬';

    getCompactTitle(): string {
        return this._getVideoTitle();
    }

    // ─── Abstract Method Implementations ─────────────────────────────────────

    getMediaId(): string {
        // Use the filename from src as the ID
        const parts = this.src.split('/');
        return parts[parts.length - 1] || this.src;
    }

    play(): void {
        this._video?.play();
    }

    pause(): void {
        this._video?.pause();
    }

    getCurrentTime(): number {
        return this._video?.currentTime ?? 0;
    }

    getDuration(): number {
        return this._video?.duration ?? 0;
    }

    seekTo(time: number): void {
        if (this._video) {
            this._video.currentTime = Math.max(0, Math.min(this.duration, time));
        }
    }

    setPlaybackRate(rate: number): void {
        if (this._video) {
            this._video.playbackRate = rate;
        }
    }

    isMediaReady(): boolean {
        return this._mediaReady;
    }

    // ─── Lifecycle ───────────────────────────────────────────────────────────

    firstUpdated() {
        // @query decorator handles the element reference
        if (this._video) {
            this._setupVideoEvents();
        }
    }

    private _setupVideoEvents() {
        if (!this._video) return;

        this._video.addEventListener('loadedmetadata', () => {
            this._mediaReady = true;
            this.duration = this._video?.duration ?? 0;
            this.startTimeUpdate();

            if (this.autoRestore) {
                this.restoreState();
            }
        });

        this._video.addEventListener('play', () => {
            this.isPlaying = true;
            this.dispatchEvent(new CustomEvent('playback-state-changed', {
                detail: { playing: true },
                bubbles: true,
                composed: true,
            }));
        });

        this._video.addEventListener('pause', () => {
            this.isPlaying = false;
            this.dispatchEvent(new CustomEvent('playback-state-changed', {
                detail: { playing: false },
                bubbles: true,
                composed: true,
            }));
        });

        this._video.addEventListener('ended', () => {
            this.isPlaying = false;
        });

        this._video.addEventListener('error', (e) => {
            console.error('[VideoLoopPlayer] Video error:', e);
            this._mediaReady = false;
        });
    }

    // ─── Render ──────────────────────────────────────────────────────────────

    // All render methods are now in the mixin

    private _getVideoTitle(): string {
        // Extract filename from src
        const parts = this.src.split('/');
        const filename = parts[parts.length - 1] || 'Video';
        const dotIndex = filename.lastIndexOf('.');
        return dotIndex > 0 ? filename.substring(0, dotIndex) : filename;
    }

    render() {
        // Structure: header → video (full mode) → controls+timeline → panels
        return html`
      <div class="lp-container" tabindex="0" @click=${() => this.focus()}>
        ${this._renderHeader()}
        
        <div class="lp-full-content">
          <div class="lp-media-wrapper">
            <video
              .src=${this.src}
              .poster=${this.poster}
              .preload=${this.preload}
              playsinline
            ></video>
            ${this.countdown !== null ? html`
              <div class="lp-countdown-overlay" @click=${() => this.cancelCountdown()}>
                <span class="lp-countdown-number">${this.countdown}</span>
              </div>
            ` : null}
          </div>
        </div>

        ${this._renderControlsAndTimeline()}

        <div class="lp-controls-panel">
          ${this._renderEditPanel()}
          ${this._renderShortcuts()}
        </div>
      </div>
            ${this._renderDeleteDialog()}
    `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'video-loop-player': VideoLoopPlayer;
    }
}
